/**
 * EasyFinder - Plan Configuration
 * SOURCE DE VÉRITÉ UNIQUE pour tous les plans et limites
 * 
 * GRILLE TARIFAIRE FINALE (v3)
 * - Gratuit : 30 docs à vie
 * - Solo : 7.90€ - particuliers
 * - Famille : 14.90€ - familles (5 membres)
 * - Pro : 29€ - indépendants, 1 module B2B au choix
 * - Team : 79€ - petites équipes (5 users), tous modules B2B
 * - Business : 149€ - TPE/PME (15 users), tous modules B2B
 * - Enterprise : sur devis
 */

export type PlanId = 'discovery' | 'solo' | 'famille' | 'pro' | 'team' | 'business' | 'enterprise';

export type B2BModule = 'fleet' | 'properties' | 'tenders' | 'subcontractors';

export type B2BAccess = 'none' | 'single' | 'all';

export interface PlanLimits {
  storage: number;              // Documents max
  sources: number;              // Sources connectées max
  aiDaily: number;              // Recherches IA par jour
  pdfMonthly: number;           // Actions PDF par mois
  pdfDailyHardCap: number | null;  // Cap journalier PDF (protection anti-abus)
  shareLinks: number;           // Liens de partage max
  users: number;                // Utilisateurs max (sièges)
  entities: number;             // Entités business max
  subcontractors: number;       // Sous-traitants max
  storageMB: number;            // Stockage en MB
  // Module B2B limits
  vehicles: number;             // Véhicules max
  properties: number;           // Biens immobiliers max
  tenders: number;              // Appels d'offres actifs max
  familyMembers: number;        // Membres famille max
}

export interface Plan {
  id: PlanId;
  name: string;
  description: string;
  priceMonthly: number;         // En euros
  priceYearly: number;          // En euros
  limits: PlanLimits;
  features: string[];
  isContactSales?: boolean;     // Pour Enterprise
  b2bAccess: B2BAccess;         // Accès aux modules B2B
}

// ============================================================================
// CONFIGURATION DES PLANS - SOURCE DE VÉRITÉ
// ============================================================================

export const PLANS: Record<PlanId, Plan> = {
  discovery: {
    id: 'discovery',
    name: 'Gratuit',
    description: 'Découvrez EasyFinder gratuitement',
    priceMonthly: 0,
    priceYearly: 0,
    b2bAccess: 'none',
    limits: {
      storage: 30,              // ⚠️ Compteur À VIE (lifetime_uploads)
      sources: 1,               // Gmail uniquement
      aiDaily: 10,
      pdfMonthly: 5,
      pdfDailyHardCap: null,
      shareLinks: 0,            // ❌ Pas de partage (restreint)
      users: 1,
      entities: 1,
      subcontractors: 0,
      storageMB: 100,
      vehicles: 0,
      properties: 0,
      tenders: 0,
      familyMembers: 0,
    },
    features: [
      'Classification IA',
      'Recherche intelligente',
      'Santé administrative',
      'Scanner documents',
      'Fusion PDF',
    ],
  },

  solo: {
    id: 'solo',
    name: 'Solo',
    description: 'Pour les particuliers organisés',
    priceMonthly: 7.90,
    priceYearly: 79,            // 2 mois offerts
    b2bAccess: 'none',
    limits: {
      storage: 300,
      sources: 2,               // Gmail + Dropbox
      aiDaily: 50,
      pdfMonthly: 30,
      pdfDailyHardCap: null,
      shareLinks: 10,
      users: 1,
      entities: 3,
      subcontractors: 0,
      storageMB: 1000,          // 1 GB
      vehicles: 0,
      properties: 0,
      tenders: 0,
      familyMembers: 0,
    },
    features: [
      'Classification IA',
      'Recherche intelligente',
      'Santé administrative',
      'Scanner documents',
      'Fusion PDF',
      'Signature PDF',
      'Tags personnalisés',
      'Alertes expiration',
      'Documents légaux',
      'Partage par lien',
      'Collections',
      'Powens (banques)',
      'Dropbox sync',
      'Support email',
    ],
  },

  famille: {
    id: 'famille',
    name: 'Famille',
    description: 'Pour gérer les documents de toute la famille',
    priceMonthly: 14.90,
    priceYearly: 149,           // 2 mois offerts
    b2bAccess: 'none',
    limits: {
      storage: 1000,
      sources: 2,               // Gmail + Dropbox
      aiDaily: 100,
      pdfMonthly: 100,
      pdfDailyHardCap: null,
      shareLinks: 50,
      users: 1,                 // 1 compte principal
      entities: 5,
      subcontractors: 0,
      storageMB: 5000,          // 5 GB
      vehicles: 0,
      properties: 0,
      tenders: 0,
      familyMembers: 5,         // 5 membres famille inclus
    },
    features: [
      'Classification IA',
      'Recherche intelligente',
      'Santé administrative',
      'Scanner documents',
      'Fusion PDF',
      'Signature PDF',
      'Tags personnalisés',
      'Alertes expiration',
      'Documents légaux',
      'Partage par lien',
      'Collections',
      'Powens (banques)',
      'Dropbox sync',
      '5 membres famille inclus',
      'Espace privé par membre',
      'Espace partagé famille',
      'Support email',
    ],
  },

  pro: {
    id: 'pro',
    name: 'Pro',
    description: 'Pour les indépendants et multi-structures',
    priceMonthly: 29,
    priceYearly: 290,           // 2 mois offerts
    b2bAccess: 'single',        // 1 module B2B au choix
    limits: {
      storage: 2000,
      sources: Infinity,        // Toutes sources (Gmail, Dropbox, Powens)
      aiDaily: 200,
      pdfMonthly: Infinity,
      pdfDailyHardCap: 100,
      shareLinks: Infinity,
      users: 1,
      entities: Infinity,       // Multi-structures illimité
      subcontractors: 20,       // Gestion sous-traitants
      storageMB: 10000,         // 10 GB
      vehicles: 10,
      properties: 10,
      tenders: 5,
      familyMembers: 0,
    },
    features: [
      'Classification IA',
      'Recherche intelligente',
      'Santé administrative',
      'Scanner documents',
      'Fusion PDF',
      'Signature PDF',
      'Tags personnalisés',
      'Alertes expiration',
      'Documents légaux',
      'Partage par lien',
      'Collections',
      'Powens (banques)',
      'Entités illimitées',
      '1 module B2B au choix',
      'Export ZIP',
      'White label',
      'Support prioritaire',
    ],
  },

  team: {
    id: 'team',
    name: 'Team',
    description: 'Pour les petites équipes',
    priceMonthly: 79,
    priceYearly: 790,           // 2 mois offerts
    b2bAccess: 'all',           // Tous les modules B2B
    limits: {
      storage: 5000,
      sources: Infinity,
      aiDaily: 500,
      pdfMonthly: Infinity,
      pdfDailyHardCap: 150,
      shareLinks: Infinity,
      users: 5,                 // 5 utilisateurs
      entities: Infinity,
      subcontractors: 50,
      storageMB: 25000,         // 25 GB
      vehicles: 30,
      properties: 30,
      tenders: 15,
      familyMembers: 0,
    },
    features: [
      'Classification IA',
      'Recherche intelligente',
      'Santé administrative',
      'Scanner documents',
      'Fusion PDF',
      'Signature PDF',
      'Tags personnalisés',
      'Alertes expiration',
      'Documents légaux',
      'Partage par lien',
      'Collections',
      'Powens (banques)',
      'Entités illimitées',
      'Tous les modules B2B',
      'Export ZIP',
      'White label',
      'Multi-utilisateurs',
      'Privé / Partagé',
      'Gestion des rôles',
      'Support prioritaire',
    ],
  },

  business: {
    id: 'business',
    name: 'Business',
    description: 'Pour les TPE/PME structurées',
    priceMonthly: 149,
    priceYearly: 1490,          // 2 mois offerts
    b2bAccess: 'all',           // Tous les modules B2B
    limits: {
      storage: 20000,
      sources: Infinity,
      aiDaily: 2000,
      pdfMonthly: Infinity,
      pdfDailyHardCap: 300,
      shareLinks: Infinity,
      users: 15,                // 15 utilisateurs
      entities: Infinity,
      subcontractors: 200,
      storageMB: 100000,        // 100 GB
      vehicles: 100,
      properties: 100,
      tenders: 50,
      familyMembers: 0,
    },
    features: [
      'Classification IA',
      'Recherche intelligente',
      'Santé administrative',
      'Scanner documents',
      'Fusion PDF',
      'Signature PDF',
      'Tags personnalisés',
      'Alertes expiration',
      'Documents légaux',
      'Partage par lien',
      'Collections',
      'Powens (banques)',
      'Entités illimitées',
      'Tous les modules B2B',
      'Export ZIP',
      'White label',
      'Multi-utilisateurs',
      'Privé / Partagé',
      'Gestion des rôles',
      'Logs d\'audit',
      'API access',
      'Support dédié',
    ],
  },

  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Solution sur mesure pour les grandes organisations',
    priceMonthly: 0,            // Sur devis
    priceYearly: 0,
    isContactSales: true,
    b2bAccess: 'all',           // Tous les modules B2B
    limits: {
      storage: Infinity,
      sources: Infinity,
      aiDaily: Infinity,
      pdfMonthly: Infinity,
      pdfDailyHardCap: null,
      shareLinks: Infinity,
      users: Infinity,
      entities: Infinity,
      subcontractors: Infinity,
      storageMB: Infinity,
      vehicles: Infinity,
      properties: Infinity,
      tenders: Infinity,
      familyMembers: 0,
    },
    features: [
      'Tout Business inclus',
      'Utilisateurs illimités',
      'Stockage illimité',
      'Volumes modules illimités',
      'SSO / SAML',
      'SLA garanti',
      'Onboarding personnalisé',
      'Conformité renforcée',
      'Hébergement dédié (option)',
      'Account Manager dédié',
    ],
  },
};

// ============================================================================
// CONFIGURATION DES ADD-ONS
// ============================================================================

export interface Addon {
  id: string;
  name: string;
  description: string;
  priceMonthly: number;
  type: 'b2b_module' | 'volume' | 'family_member';
  extraUnits?: number;
  moduleKey?: B2BModule;
}

export const ADDONS: Record<string, Addon> = {
  b2b_module_extra: {
    id: 'b2b_module_extra',
    name: 'Module B2B supplémentaire',
    description: 'Ajoutez un module métier supplémentaire à votre plan Pro',
    priceMonthly: 12,
    type: 'b2b_module',
  },
  pack_vehicles: {
    id: 'pack_vehicles',
    name: 'Pack Véhicules (+20)',
    description: 'Augmentez votre limite de véhicules de 20',
    priceMonthly: 10,
    type: 'volume',
    extraUnits: 20,
  },
  pack_properties: {
    id: 'pack_properties',
    name: 'Pack Biens Immo (+20)',
    description: 'Augmentez votre limite de biens immobiliers de 20',
    priceMonthly: 10,
    type: 'volume',
    extraUnits: 20,
  },
  pack_subcontractors: {
    id: 'pack_subcontractors',
    name: 'Pack Prestataires (+50)',
    description: 'Augmentez votre limite de prestataires de 50',
    priceMonthly: 15,
    type: 'volume',
    extraUnits: 50,
  },
  pack_tenders: {
    id: 'pack_tenders',
    name: 'Pack Appels d\'offres (+10)',
    description: 'Augmentez votre limite d\'appels d\'offres actifs de 10',
    priceMonthly: 5,
    type: 'volume',
    extraUnits: 10,
  },
  family_member_extra: {
    id: 'family_member_extra',
    name: 'Membre Famille supplémentaire',
    description: 'Ajoutez un membre à votre plan Famille (max 3)',
    priceMonthly: 2.90,
    type: 'family_member',
    extraUnits: 1,
  },
};

// ============================================================================
// CONFIGURATION DES MODULES B2B
// ============================================================================

export const B2B_MODULES: Record<B2BModule, { name: string; icon: string; description: string }> = {
  fleet: {
    name: 'Flotte',
    icon: 'Car',
    description: 'Gestion véhicules professionnels : carte grise, assurance, contrôle technique',
  },
  properties: {
    name: 'Immobilier',
    icon: 'Building2',
    description: 'Gestion biens locatifs : bail, diagnostics, quittances, portail locataire',
  },
  tenders: {
    name: 'Appels d\'offres',
    icon: 'FileText',
    description: 'Réponse marchés publics : analyse DCE, checklist documents, suivi deadlines',
  },
  subcontractors: {
    name: 'Prestataires',
    icon: 'Users',
    description: 'Conformité sous-traitants : KBIS, URSSAF, RC Pro, Décennale, portail dépôt',
  },
};

// ============================================================================
// MAPPING ANCIENS → NOUVEAUX SLUGS (Migration)
// ============================================================================

export const LEGACY_PLAN_MAPPING: Record<string, PlanId> = {
  'trial': 'discovery',
  'classique': 'solo',
  'personal': 'solo',
  'premium': 'pro',
  'entreprise': 'business',
};

// ============================================================================
// UTILITAIRES
// ============================================================================

/**
 * Récupère un plan par son ID (nouveau ou ancien slug)
 */
export function getPlan(planIdOrSlug: string): Plan {
  // Essayer d'abord avec le nouveau ID
  if (planIdOrSlug in PLANS) {
    return PLANS[planIdOrSlug as PlanId];
  }
  
  // Sinon, essayer avec le mapping legacy
  const mappedId = LEGACY_PLAN_MAPPING[planIdOrSlug];
  if (mappedId) {
    return PLANS[mappedId];
  }
  
  // Fallback sur discovery
  console.warn(`Plan inconnu: ${planIdOrSlug}, fallback sur discovery`);
  return PLANS.discovery;
}

/**
 * Vérifie si une limite est "illimitée"
 */
export function isUnlimited(value: number): boolean {
  return value === Infinity || value === null;
}

/**
 * Formate une limite pour l'affichage
 */
export function formatLimit(value: number): string {
  if (isUnlimited(value)) {
    return 'Illimité';
  }
  return value.toLocaleString('fr-FR');
}

/**
 * Vérifie si un plan a une feature
 */
export function hasFeature(plan: Plan, feature: string): boolean {
  return plan.features.includes(feature);
}

/**
 * Vérifie si le plan utilise le compteur lifetime (plan gratuit)
 */
export function usesLifetimeCounter(planId: PlanId): boolean {
  return planId === 'discovery';
}

/**
 * Vérifie si le plan est payant
 */
export function isPaidPlan(planId: PlanId): boolean {
  return planId !== 'discovery';
}

/**
 * Vérifie si le plan supporte le multi-utilisateurs
 */
export function supportsMultiUsers(planId: PlanId): boolean {
  return planId === 'team' || planId === 'business' || planId === 'enterprise';
}

/**
 * Vérifie si le plan a accès aux modules B2B
 */
export function hasB2BAccess(planId: PlanId): boolean {
  const plan = PLANS[planId];
  return plan?.b2bAccess !== 'none';
}

/**
 * Vérifie si le plan a accès à un module B2B spécifique
 */
export function hasModuleAccess(planId: PlanId, module: B2BModule, selectedModule?: B2BModule | null): boolean {
  const plan = PLANS[planId];
  if (!plan) return false;
  
  if (plan.b2bAccess === 'none') return false;
  if (plan.b2bAccess === 'all') return true;
  
  // Plan 'single' : vérifie si c'est le module sélectionné
  return selectedModule === module;
}

/**
 * Récupère tous les plans affichables (excluant Enterprise pour certains contextes)
 */
export function getDisplayPlans(includeEnterprise = true): Plan[] {
  const allPlans = Object.values(PLANS);
  return includeEnterprise 
    ? allPlans 
    : allPlans.filter(p => p.id !== 'enterprise');
}

/**
 * Récupère les plans B2B (Pro+)
 */
export function getB2BPlans(): Plan[] {
  return Object.values(PLANS).filter(p => p.b2bAccess !== 'none');
}
